import * as React from 'react';

/**
 * Button visual style variants
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

/**
 * Button size options
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant of the button
   * @default 'primary'
   */
  variant?: ButtonVariant;
  /**
   * Size of the button
   * @default 'md'
   */
  size?: ButtonSize;
  /**
   * Whether the button is in a loading state
   * @default false
   */
  loading?: boolean;
  /**
   * Icon to display on the left side of the button text
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to display on the right side of the button text
   */
  rightIcon?: React.ReactNode;
}

/**
 * Spinner component for loading state
 */
const Spinner: React.FC<{ size: ButtonSize; className?: string }> = ({
  size,
  className = '',
}) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

/**
 * Button component following the insurance design system specifications.
 *
 * Features:
 * - Multiple variants: primary, secondary, ghost, danger, success
 * - Three sizes: sm (32px), md (40px), lg (48px)
 * - Loading state with spinner
 * - Icon support (left, right, or icon-only)
 * - Accessible with proper focus states
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      className = '',
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const isIconOnly = !children && (leftIcon || rightIcon);

    // Base classes
    const baseClasses = [
      'inline-flex items-center justify-center',
      'font-medium',
      'rounded-md',
      'transition-all duration-150 ease-out',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ].join(' ');

    // Size classes
    const sizeClasses: Record<ButtonSize, string> = {
      sm: isIconOnly ? 'h-8 w-8 text-sm' : 'h-8 px-3 text-sm',
      md: isIconOnly ? 'h-10 w-10 text-sm' : 'h-10 px-4 text-sm',
      lg: isIconOnly ? 'h-12 w-12 text-base' : 'h-12 px-5 text-base',
    };

    // Variant classes (using semantic Tailwind tokens)
    const variantClasses: Record<ButtonVariant, string> = {
      primary: [
        'bg-primary-700 text-white',
        'hover:bg-primary-800',
        'active:bg-primary-900 active:scale-[0.98]',
        'focus:ring-primary-200',
      ].join(' '),
      secondary: [
        'bg-transparent text-primary-700 border border-primary-200',
        'hover:bg-primary-50',
        'active:bg-primary-100 active:scale-[0.98]',
        'focus:ring-primary-200',
      ].join(' '),
      ghost: [
        'bg-transparent text-secondary-700',
        'hover:bg-secondary-100',
        'active:bg-secondary-200 active:scale-[0.98]',
        'focus:ring-secondary-300',
      ].join(' '),
      danger: [
        'bg-error-700 text-white',
        'hover:bg-error-800',
        'active:bg-error-900 active:scale-[0.98]',
        'focus:ring-error-200',
      ].join(' '),
      success: [
        'bg-success-700 text-white',
        'hover:bg-success-800',
        'active:bg-success-900 active:scale-[0.98]',
        'focus:ring-success-200',
      ].join(' '),
    };

    // Icon spacing based on size
    const iconSpacing = {
      sm: 'gap-1.5',
      md: 'gap-2',
      lg: 'gap-2.5',
    };

    const combinedClasses = [
      baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      iconSpacing[size],
      className,
    ].join(' ');

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={combinedClasses}
        aria-busy={loading}
        {...props}
      >
        {loading && <Spinner size={size} />}
        {!loading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
        {children && <span className={loading ? 'opacity-0 w-0 overflow-hidden' : ''}>{children}</span>}
        {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, Spinner };
